import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { Box, Button, Chip, LinearProgress, Stack, Tab, Tabs, Typography } from '@mui/material';
import { PowerSettingsNew as PowerSettingsNewIcon } from '@mui/icons-material';

import PageHeader from '../../../common/page_header';
import { ROUTE_PRODUCTS } from '../../../../core/constants';
import { getProductAction, getProductState, invalidateProduct } from '../../../../features/product/get_product';
import ProductForm from './forms/product_form';
import ProductDetailsForm from './forms/product_details_form';
import ProductSalesDataForm from './forms/product_sales_data_form';
import ProductEansForm from './forms/product_eans_form';
import { getConstantsAction, getConstantsState, invalidateConstants } from '../../../../features/product/get_constants';
import useNotification from '../../../../core/hooks/use_notification';
import { getAllFoodPartnersNotificationData, getAllHeatingProgramsNotificationData, getAllProducersNotificationData, getConstantsNotificationData, getProductNotificationData, updateProductStatusNotificationData } from '../../../../core/utils/notification_utils';
import QRCodeDownload from '../../../common/qr_code_download';
import { getAllFoodPartnersAction, getAllFoodPartnersState } from '../../../../features/food_partner/get_all_food_partners';
import { getAllProducersAction, getAllProducersState } from '../../../../features/product/get_all_producers';
import { getAllHeatingProgramsAction, getAllHeatingProgramsState } from '../../../../features/product/get_all_heating_programs';
import { getDepositsAction, getDepositsState } from '../../../../features/price/get_deposits';
import { patchProductAction } from '../../../../features/product/patch_product';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import PriceListFormPage from './forms/price_list_form';
import ProductBatchesStock from './forms/product_batches_stock';

const ProductFormsPage = () => {
    const { showNotification, showErrorMessage } = useNotification();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const { productId } = useParams();
    const location = useLocation();

    const isEdit = !!productId;

    const getProduct = useSelector(getProductState);
    const getConstants = useSelector(getConstantsState);
    const getAllHeatingPrograms = useSelector(getAllHeatingProgramsState);
    const getDeposits = useSelector(getDepositsState);
    const getFoodPartners = useSelector(getAllFoodPartnersState);
    const getProducers = useSelector(getAllProducersState);

    const [tab, setTab] = useState(0);

    useEffect(() => {
        dispatch(getConstantsAction())
            .then(response => showErrorMessage(getConstantsNotificationData(response)));
        dispatch(getAllHeatingProgramsAction())
            .then(response => showErrorMessage(getAllHeatingProgramsNotificationData(response)));
        dispatch(getDepositsAction());
        dispatch(getAllFoodPartnersAction())
            .then(response => showErrorMessage(getAllFoodPartnersNotificationData(response)));
        dispatch(getAllProducersAction())
            .then(response => showErrorMessage(getAllProducersNotificationData(response)))

        return () => {
            dispatch(invalidateConstants());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        if (isEdit) {
            dispatch(getProductAction({ id: productId }))
                .then(response => showErrorMessage(getProductNotificationData(response)));
        }

        return () => {
            dispatch(invalidateProduct());
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, productId, isEdit]);

    const onPatchProductStatus = () => {
        const isActive = getProduct?.data?.active;
        const command = isActive ? 'DEACTIVATE' : 'ACTIVATE';

        dispatch(patchProductAction({ id: productId, command }))
            .then(response => {
                showNotification(updateProductStatusNotificationData(response))
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    dispatch(getProductAction({ id: productId }))
                        .then(response => showErrorMessage(getProductNotificationData(response)));
                }
            })
    };

    const renderView = () => {
        if (getConstants?.loading || getAllHeatingPrograms?.loading || getDeposits?.loading || getFoodPartners?.loading || getProducers?.loading || (isEdit && getProduct?.loading)) {
            return (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            );
        }

        if (getConstants?.data && getAllHeatingPrograms?.data) {
            if (tab === 0) {
                return <ProductForm isEdit={ isEdit } />;
            } else if (tab === 1 && isEdit) {
                return <ProductDetailsForm productId={ productId } />;
            } else if (tab === 2 && isEdit) {
                return <ProductSalesDataForm productId={ productId } />;
            } else if (tab === 3 && isEdit) {
                return <PriceListFormPage productId={ productId } />;
            } else if (tab === 4 && isEdit) {
                return <ProductEansForm productId={ productId } />;
            } else if (tab === 5 && isEdit) {
                return <ProductBatchesStock productId={ productId } />;
            }
        }

        return null;
    };

    return (
        <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1 } }>
            { (!isEdit || getProduct?.data) && (
                <Box sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1, gap: 3 } }>

                    <PageHeader
                        title={ isEdit ? (getProduct?.data?.workingName || 'Edycja produktu') : 'Dodawanie nowego produktu' }
                        showBack={ true }
                        onBack={ () => navigate(ROUTE_PRODUCTS, { state: { queryParams: location?.state?.queryParams } }) }
                        titleAdornment={
                            isEdit && getProduct?.data && (
                                <Stack direction="row" spacing={ 2 } alignItems="center" sx={ { mt: { xs: 1, sm: 0 }, ml: { sm: 2 } } }>
                                    <Typography variant="h6" color="textSecondary">
                                        ID: { getProduct?.data?.id }
                                    </Typography>
                                    <Chip
                                        label={ getProduct?.data?.active ? "Aktywny" : "Nieaktywny" }
                                        color={ getProduct?.data?.active ? "success" : "warning" }
                                        sx={ { fontWeight: 600 } }
                                    />
                                </Stack>
                            )
                        }
                        actions={
                            isEdit && getProduct?.data && (
                                <Stack direction="row" spacing={ 2 } alignItems="center">
                                    <Button
                                        variant="outlined"
                                        color={ getProduct?.data?.active ? "error" : "success" }
                                        startIcon={ <PowerSettingsNewIcon /> }
                                        onClick={ onPatchProductStatus }
                                    >
                                        { getProduct?.data?.active ? "Dezaktywuj" : "Aktywuj" }
                                    </Button>

                                    <QRCodeDownload
                                        productId={ getProduct?.data?.id }
                                        productName={ getProduct?.data?.workingName }
                                    />
                                </Stack>
                            )
                        }
                    />

                    <Box>
                        <Box sx={ { borderBottom: 1, borderColor: 'divider' } }>
                            <Tabs value={ tab } onChange={ (_, value) => setTab(value) } variant="scrollable" scrollButtons="auto">
                                <Tab label="Informacje ogólne"/>
                                { isEdit && <Tab label="Treści wielojęzyczne" /> }
                                { isEdit && <Tab label="Dane sprzedażowe" /> }
                                { isEdit && <Tab label="Cenniki" /> }
                                { isEdit && <Tab label="Kody EAN" /> }
                                { isEdit && <Tab label="Partie" /> }
                            </Tabs>
                        </Box>
                    </Box>

                    <Box sx={ { pt: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 } }>
                        { renderView() }
                    </Box>
                </Box>
            ) }

            { isEdit && (!getProduct?.data || getProduct?.loading) && (
                <Box mt={ 6 }>
                    <LinearProgress/>
                </Box>
            ) }
        </Box>
    );
};

export default ProductFormsPage;